import { FATHOM_TO_METER, FOOT_TO_METER } from '@racemap/utilities/consts/units';
import { UnitType } from '@racemap/utilities/types/units';
import { clamp } from 'lodash';
import { SHADOW_TRACK_STEP, STATISTIC_FILTER_SIZE } from 'src/lib/player/constants';
import type { RenderInfoBuffer } from 'src/lib/player/RenderInfoManager';
import type { XY } from 'src/lib/player/store';
import type TrackView from 'src/lib/player/utils/TrackView';
import type { Bounds } from '../InfoSheet/SVGChart';

export const MAX_DOT_COUNT = 1000;
export const DOT_RADIUS = 3;

const MIN_Y_RANGE_METER = 100;
const MIN_Y_RANGE_FOOT = 300;
const MIN_Y_RANGE_FATHOM = 50;

export const MIN_ZOOM_RANGE_METER = 50;

export type ViewRange = {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
};

export type ZoomRange = { minX: number; maxX: number };

export function projectX(dataX: number, viewRange: ViewRange, bounds: Bounds): number {
  const span = viewRange.maxX - viewRange.minX;
  if (span <= 0) return bounds.paddingLeft;
  return bounds.paddingLeft + ((dataX - viewRange.minX) / span) * bounds.innerWidth;
}

export function unprojectX(svgX: number, viewRange: ViewRange, bounds: Bounds): number {
  if (bounds.innerWidth <= 0) return viewRange.minX;
  return (
    viewRange.minX +
    ((svgX - bounds.paddingLeft) / bounds.innerWidth) * (viewRange.maxX - viewRange.minX)
  );
}

export function isDataXInView(dataX: number, viewRange: ViewRange): boolean {
  return dataX >= viewRange.minX && dataX <= viewRange.maxX;
}

export function minYRangeByUnitType(unitType: UnitType): number {
  switch (unitType) {
    case UnitType.IMPERIAL:
      return MIN_Y_RANGE_FOOT * FOOT_TO_METER;
    case UnitType.NAUTICAL:
      return MIN_Y_RANGE_FATHOM * FATHOM_TO_METER;
    case UnitType.METRIC:
      return MIN_Y_RANGE_METER;
    default:
      return MIN_Y_RANGE_METER;
  }
}

export function computeViewRange(
  filteredTrack: Array<XY>,
  unitType: UnitType,
  elevationChartOptions: { min?: number | null; max?: number | null } | null | undefined,
  zoom: ZoomRange | null = null,
): ViewRange {
  let dataMaxX = 0;
  let dataMaxY = 0;
  for (const point of filteredTrack) {
    dataMaxX = Math.max(dataMaxX, point.x);
    dataMaxY = Math.max(dataMaxY, point.y);
  }
  let minX = zoom != null ? Math.max(0, zoom.minX) : 0;
  const maxX = zoom != null ? Math.min(dataMaxX, zoom.maxX) : dataMaxX;
  // Normalize range: ensure minX <= maxX (can become invalid when zoom exceeds dataMaxX)
  minX = Math.min(minX, maxX);
  const minY = elevationChartOptions?.min ?? 0;
  const maxY = elevationChartOptions?.max ?? Math.max(dataMaxY, minYRangeByUnitType(unitType));
  return { minX, maxX, minY, maxY };
}

export function zoomAroundPointer(
  viewRange: ViewRange,
  pointerDataX: number,
  factor: number,
  dataMaxX: number,
): ZoomRange | null {
  const span = viewRange.maxX - viewRange.minX;
  if (span <= 0) return null;
  const relative = (pointerDataX - viewRange.minX) / span;
  const newSpan = Math.min(Math.max(span * factor, MIN_ZOOM_RANGE_METER), dataMaxX);
  let newMinX = pointerDataX - relative * newSpan;
  let newMaxX = newMinX + newSpan;
  if (newMinX < 0) {
    newMinX = 0;
    newMaxX = newSpan;
  }
  if (newMaxX > dataMaxX) {
    newMaxX = dataMaxX;
    newMinX = dataMaxX - newSpan;
  }
  if (newMinX <= 0 && newMaxX >= dataMaxX) return null;
  return { minX: newMinX, maxX: newMaxX };
}

export function* chartDataFromShadowTrack(
  shadowTrack: TrackView | null,
): Generator<XY, void, void> {
  if (shadowTrack == null) return;
  for (let i = 0; i < shadowTrack.size(); i++) {
    const elevation = shadowTrack.getElevation(i);
    const correctedElevation = Number.isNaN(elevation) ? 0 : elevation;
    yield { x: i * SHADOW_TRACK_STEP, y: correctedElevation };
  }
}

export function getInterpolatedElevation(track: Array<XY>, x: number): number {
  if (track.length === 0) {
    return Number.NaN;
  }
  const hiIdx = track.findIndex((xy) => xy.x >= x);
  if (hiIdx > 0) {
    const hi = track[hiIdx];
    const lo = track[hiIdx - 1];
    const fraction = (x - lo.x) / (hi.x - lo.x);
    return lo.y * (1 - fraction) + hi.y * fraction;
  }
  if (hiIdx === 0) {
    return track[0].y;
  }
  if (track[track.length - 1].x < x) {
    return track[track.length - 1].y;
  }
  return Number.NaN;
}

export function getInterpolatedY(
  elevation: number,
  minY: number,
  maxY: number,
  bounds: Bounds,
): number {
  return maxY === minY
    ? bounds.paddingTop + 0.5 * bounds.innerHeight
    : bounds.paddingTop +
        (1 - (clamp(elevation, minY, maxY) - minY) / (maxY - minY)) * bounds.innerHeight;
}

export function getInterpolatedStatistic(track: Array<number>, index: number): number {
  const loIdx = Math.floor(index / STATISTIC_FILTER_SIZE);
  const hiIdx = Math.ceil(index / STATISTIC_FILTER_SIZE);
  const fraction = (index / STATISTIC_FILTER_SIZE) % 1;
  if (loIdx >= 0 && loIdx < track.length) {
    if (hiIdx >= 0 && hiIdx < track.length) {
      return track[loIdx] * (1 - fraction) + track[hiIdx] * fraction;
    }
    return track[loIdx];
  }
  return Number.NaN;
}

export function hitTestDot(
  renderInfoBuffer: RenderInfoBuffer,
  bounds: Bounds,
  viewRange: ViewRange,
  x: number,
) {
  if (renderInfoBuffer.length > MAX_DOT_COUNT) return null;
  for (let i = 0; i < renderInfoBuffer.length; i++) {
    const isVisible = renderInfoBuffer.getIsVisible(i);
    const offset = renderInfoBuffer.getOffset(i);
    const opacity = renderInfoBuffer.getOpacity(i);
    if (
      isVisible &&
      offset >= 0 &&
      !Number.isNaN(offset) &&
      opacity > 0 &&
      isDataXInView(offset, viewRange)
    ) {
      const starterDotX = projectX(offset, viewRange, bounds);
      if (x >= starterDotX - DOT_RADIUS && x <= starterDotX + DOT_RADIUS) {
        return renderInfoBuffer.starters[i];
      }
    }
  }
  return null;
}
